import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  AttachFile,
  Close,
  CloudUpload,
  LibraryBooks,
} from "@material-ui/icons";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/ApiKeyContext";
import { getApiBaseUrl } from "../config";

// Formato CNJ obrigatório no backend: XXXXXXX-XX.XXXX.X.XX.XXXX (resolução 65).
const CNJ_PATTERN = /^\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}$/;

// Helper que vai aplicando a máscara CNJ enquanto o usuário digita. Aceita
// dígitos colados/pegados de qualquer lugar (limpa não-dígitos) e formata
// até onde tem dígitos disponíveis.
const formatCNJ = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 20);
  if (!digits) return "";
  let out = digits.slice(0, 7);
  if (digits.length > 7) out += "-" + digits.slice(7, 9);
  if (digits.length > 9) out += "." + digits.slice(9, 13);
  if (digits.length > 13) out += "." + digits.slice(13, 14);
  if (digits.length > 14) out += "." + digits.slice(14, 16);
  if (digits.length > 16) out += "." + digits.slice(16, 20);
  return out;
};

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid var(--ikaro-border-default)",
  },
  titleText: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  titleIcon: {
    color: "var(--ikaro-orange-primary)",
  },
  dialogContent: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  description: {
    fontSize: "0.9rem",
    color: "var(--ikaro-text-secondary)",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  fileBox: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    borderRadius: 4,
    border: "1px dashed var(--ikaro-border-default)",
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
  },
  fileLabel: {
    fontSize: "0.92rem",
    fontWeight: 600,
  },
  fileHelper: {
    fontSize: "0.8rem",
    color: "var(--ikaro-text-secondary)",
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    flexWrap: "wrap",
  },
  fileSelected: {
    fontSize: "0.85rem",
    color: "var(--ikaro-text-primary)",
    fontFamily: "monospace",
    wordBreak: "break-all",
  },
  duplicateAlert: {
    marginTop: theme.spacing(1),
  },
  hiddenInput: {
    display: "none",
  },
}));

const TeseImportModal = ({ open, onClose, cellId, onJobCreated }) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const { token } = useAuth();
  const API_BASE_URL = getApiBaseUrl();

  // form state
  const [numeroProcesso, setNumeroProcesso] = useState("");
  const [inicialFile, setInicialFile] = useState(null);
  const [contestacaoFile, setContestacaoFile] = useState(null);

  // ui state
  const [submitting, setSubmitting] = useState(false);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [pendingDuplicate, setPendingDuplicate] = useState(null); // { exists, id_documento, nome_arquivo, data_insercao }
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const inicialInputRef = useRef(null);
  const contestacaoInputRef = useRef(null);

  // reset state when modal closes
  useEffect(() => {
    if (!open) {
      setNumeroProcesso("");
      setInicialFile(null);
      setContestacaoFile(null);
      setSubmitting(false);
      setCheckingDuplicate(false);
      setPendingDuplicate(null);
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  const cnjValid = useMemo(
    () => CNJ_PATTERN.test(numeroProcesso),
    [numeroProcesso]
  );

  const canSubmit =
    cnjValid &&
    !!inicialFile &&
    !!contestacaoFile &&
    !submitting &&
    !checkingDuplicate &&
    !success; // bloqueia novo submit após sucesso até fechar o modal

  // ----- handlers -----

  const handleNumeroChange = useCallback((e) => {
    setNumeroProcesso(formatCNJ(e.target.value));
    setPendingDuplicate(null); // qualquer mudança invalida verificação anterior
  }, []);

  const handleFileSelect = useCallback((kind, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError(t("contestacao.cellParams.thesisImport.errorMissingFiles"));
      return;
    }
    setError(null);
    if (kind === "inicial") setInicialFile(file);
    if (kind === "contestacao") setContestacaoFile(file);
    setPendingDuplicate(null);
  }, [t]);

  const _postImport = useCallback(
    async (overwrite) => {
      const formData = new FormData();
      formData.append("cell_id", String(cellId));
      formData.append("numero_processo", numeroProcesso);
      formData.append("arquivo_inicial", inicialFile);
      formData.append("arquivo_contestacao", contestacaoFile);
      formData.append("overwrite", overwrite ? "true" : "false");

      const response = await fetch(
        `${API_BASE_URL}/contestacao_teses/import_tese`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );
      if (!response.ok) {
        let detail = null;
        try {
          const data = await response.json();
          detail = data?.detail;
        } catch {
          /* não-JSON: ignora */
        }
        // 409 sem overwrite vem com detail.error === "duplicate".
        if (response.status === 409 && detail && typeof detail === "object") {
          return { duplicateDetail: detail };
        }
        const msg =
          typeof detail === "string"
            ? detail
            : detail?.message ||
              t("contestacao.cellParams.thesisImport.errorCreateJob");
        throw new Error(msg);
      }
      return await response.json();
    },
    [API_BASE_URL, cellId, numeroProcesso, inicialFile, contestacaoFile, token, t]
  );

  const handleSubmit = useCallback(async () => {
    setError(null);
    setSuccess(null);

    if (!cnjValid) {
      setError(t("contestacao.cellParams.thesisImport.processNumberInvalid"));
      return;
    }
    if (!numeroProcesso) {
      setError(t("contestacao.cellParams.thesisImport.errorMissingProcesso"));
      return;
    }
    if (!inicialFile || !contestacaoFile) {
      setError(t("contestacao.cellParams.thesisImport.errorMissingFiles"));
      return;
    }

    // Passo 1: check_duplicate antes de gastar a banda do upload em vão.
    setCheckingDuplicate(true);
    try {
      const url =
        `${API_BASE_URL}/contestacao_teses/check_duplicate?` +
        `cell_id=${cellId}&numero_processo=${encodeURIComponent(
          numeroProcesso
        )}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(
          t("contestacao.cellParams.thesisImport.errorCheckDuplicate")
        );
      }
      const data = await response.json();
      setCheckingDuplicate(false);

      if (data?.exists) {
        // Não chamamos import_tese ainda; mostramos o aviso de duplicate
        // pra usuário confirmar a sobrescrita.
        setPendingDuplicate(data);
        return;
      }
    } catch (e) {
      setCheckingDuplicate(false);
      setError(
        e?.message ||
          t("contestacao.cellParams.thesisImport.errorCheckDuplicate")
      );
      return;
    }

    // Passo 2: import sem overwrite (não havia duplicate).
    setSubmitting(true);
    try {
      const data = await _postImport(false);
      // Edge case: alguém criou duplicate entre o check e o submit. O endpoint
      // responde 409 e _postImport devolve { duplicateDetail }. Tratamos
      // mostrando o mesmo aviso do passo 1.
      if (data.duplicateDetail) {
        setSubmitting(false);
        setPendingDuplicate({
          exists: true,
          id_documento: data.duplicateDetail.id_documento,
          nome_arquivo: null,
        });
        return;
      }
      setSubmitting(false);
      setSuccess(
        t("contestacao.cellParams.thesisImport.successCreated", {
          jobId: data.job_id,
        })
      );
      if (typeof onJobCreated === "function") onJobCreated(data);
    } catch (e) {
      setSubmitting(false);
      setError(
        e?.message || t("contestacao.cellParams.thesisImport.errorCreateJob")
      );
    }
  }, [
    cnjValid,
    numeroProcesso,
    inicialFile,
    contestacaoFile,
    cellId,
    API_BASE_URL,
    token,
    _postImport,
    t,
    onJobCreated,
  ]);

  const handleOverwriteConfirm = useCallback(async () => {
    setPendingDuplicate(null);
    setSubmitting(true);
    setError(null);
    try {
      const data = await _postImport(true);
      setSubmitting(false);
      setSuccess(
        t("contestacao.cellParams.thesisImport.successCreated", {
          jobId: data.job_id,
        })
      );
      if (typeof onJobCreated === "function") onJobCreated(data);
    } catch (e) {
      setSubmitting(false);
      setError(
        e?.message || t("contestacao.cellParams.thesisImport.errorCreateJob")
      );
    }
  }, [_postImport, t, onJobCreated]);

  const handleOverwriteCancel = useCallback(() => {
    setPendingDuplicate(null);
  }, []);

  // ----- render -----

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box className={classes.titleText}>
          <LibraryBooks className={classes.titleIcon} />
          <Typography variant="h6">
            {t("contestacao.cellParams.thesisImport.modalTitle")}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.description}>
          {t("contestacao.cellParams.thesisImport.modalDescription")}
        </Typography>

        {/* Número do processo */}
        <Box className={classes.field}>
          <TextField
            label={t(
              "contestacao.cellParams.thesisImport.processNumberLabel"
            )}
            placeholder={t(
              "contestacao.cellParams.thesisImport.processNumberPlaceholder"
            )}
            value={numeroProcesso}
            onChange={handleNumeroChange}
            error={numeroProcesso.length > 0 && !cnjValid}
            helperText={
              numeroProcesso.length > 0 && !cnjValid
                ? t(
                    "contestacao.cellParams.thesisImport.processNumberInvalid"
                  )
                : t("contestacao.cellParams.thesisImport.processNumberHelper")
            }
            disabled={submitting || checkingDuplicate}
            inputProps={{ maxLength: 25 }}
            variant="outlined"
            fullWidth
            size="small"
          />
        </Box>

        {/* PDF inicial */}
        <Box className={classes.fileBox}>
          <Typography className={classes.fileLabel}>
            {t("contestacao.cellParams.thesisImport.inicialLabel")}
          </Typography>
          <Typography className={classes.fileHelper}>
            {t("contestacao.cellParams.thesisImport.inicialHelper")}
          </Typography>
          <input
            ref={inicialInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className={classes.hiddenInput}
            onChange={(e) =>
              handleFileSelect("inicial", e.target.files && e.target.files[0])
            }
          />
          <Box className={classes.fileRow}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachFile />}
              onClick={() => inicialInputRef.current?.click()}
              disabled={submitting || checkingDuplicate}
            >
              {inicialFile
                ? t("contestacao.cellParams.thesisImport.replaceFile")
                : t("contestacao.cellParams.thesisImport.selectFile")}
            </Button>
            {inicialFile && (
              <Typography className={classes.fileSelected}>
                {t("contestacao.cellParams.thesisImport.selectedFile", {
                  name: inicialFile.name,
                })}
              </Typography>
            )}
          </Box>
        </Box>

        {/* PDF contestação */}
        <Box className={classes.fileBox}>
          <Typography className={classes.fileLabel}>
            {t("contestacao.cellParams.thesisImport.contestacaoLabel")}
          </Typography>
          <Typography className={classes.fileHelper}>
            {t("contestacao.cellParams.thesisImport.contestacaoHelper")}
          </Typography>
          <input
            ref={contestacaoInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className={classes.hiddenInput}
            onChange={(e) =>
              handleFileSelect(
                "contestacao",
                e.target.files && e.target.files[0]
              )
            }
          />
          <Box className={classes.fileRow}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachFile />}
              onClick={() => contestacaoInputRef.current?.click()}
              disabled={submitting || checkingDuplicate}
            >
              {contestacaoFile
                ? t("contestacao.cellParams.thesisImport.replaceFile")
                : t("contestacao.cellParams.thesisImport.selectFile")}
            </Button>
            {contestacaoFile && (
              <Typography className={classes.fileSelected}>
                {t("contestacao.cellParams.thesisImport.selectedFile", {
                  name: contestacaoFile.name,
                })}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Aviso de duplicate (pré-confirmação de overwrite) */}
        {pendingDuplicate && (
          <Alert
            severity="warning"
            className={classes.duplicateAlert}
            action={
              <Box display="flex" gridGap={8}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleOverwriteCancel}
                  disabled={submitting}
                >
                  {t("contestacao.cellParams.thesisImport.duplicateCancel")}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleOverwriteConfirm}
                  disabled={submitting}
                >
                  {t("contestacao.cellParams.thesisImport.duplicateOverwrite")}
                </Button>
              </Box>
            }
          >
            <Typography variant="subtitle2">
              {t("contestacao.cellParams.thesisImport.duplicateTitle")}
            </Typography>
            <Typography variant="body2">
              {t("contestacao.cellParams.thesisImport.duplicateMessage", {
                numero: numeroProcesso,
                nome: pendingDuplicate.nome_arquivo || "—",
              })}
            </Typography>
          </Alert>
        )}

        {/* Erro */}
        {error && <Alert severity="error">{error}</Alert>}

        {/* Sucesso */}
        {success && <Alert severity="success">{success}</Alert>}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={submitting}>
          {t("contestacao.cellParams.thesisImport.close")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          startIcon={
            submitting || checkingDuplicate ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CloudUpload />
            )
          }
        >
          {checkingDuplicate
            ? t("contestacao.cellParams.thesisImport.checkingDuplicate")
            : submitting
            ? t("contestacao.cellParams.thesisImport.submitting")
            : t("contestacao.cellParams.thesisImport.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeseImportModal;
